import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ROWS_PER_PAGE } from "../App";
import { AppToolbar } from "../components/AppToolbar";
import TableBasedCrud from "../external/ensolvers-core-frontend-mui/components/TableBaseCrud";
import RoleUtils from "../utils/RoleUtils";
import { Autocomplete, Box, Button, TextField, Typography } from "@mui/material";
import Site from "../models/Site";
import AddIcon from "@mui/icons-material/Add";
import { SelectComponent } from "../components/SelectComponent";
import { SitesRoleOptions } from "../utils/enums/EnsolversStaffRoles";
import AutoCompleteUIDTO from "../dto/AutoCompleteUIDTO";
import OpsStringUtils from "../utils/OpsStringUtils";
import SiteCreateDialog from "../components/AdminSite/SiteCreateDialog";
import { AppDialog } from "../components/AppDialog";
import { useSiteList } from "../hooks/useSiteList";
import SiteViewDialog from "../components/AdminSite/SiteViewDialog";
import SiteEditDialog from "../components/AdminSite/SiteEditDialog";
import OpsAvatarComponent from "../components/OpsAvatarComponent";
import NoPhotographyIcon from "@mui/icons-material/NoPhotography";
import useLocalStorage, { STORAGE_KEYS } from "../hooks/useLocalStorage";
import { BooleanSegmented } from "../components/SegmentedControl";
import FilterDialog, { FilterDialogButton } from "../components/FilterDialog";
import { makeStyles } from "@mui/styles";
import useWindowSize from "../hooks/useWindowSize";
import { useLocation } from "react-router-dom";
import { Routes } from "../types/Routes";
import useUpdateUrl from "../hooks/useUpdateUrl";
import { AppContainerFullHeight } from "../components/AppContainerFullHeight";
import useTableHeight from "../hooks/useTableHeight";
import ActionButton, { ActionsMenuEntryIcons } from "../components/Worklog/ActionButton";

import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import VisibilityIcon from "@mui/icons-material/Visibility";

import { customButtonBuilder, customButtonsContainer, gridButtonContainer } from "../components/Main/tableStyles";
import { useToolbarActions } from "../components/Main/ToolbarActions";
import { filtersBarStyle, Token } from "../theme";

export default function SiteList() {
  const { toolbarActionsSlot, addButtonInToolbar } = useToolbarActions();
  const { t } = useTranslation();
  const classes = useStyles();

  const [search, setSearch] = useState<string>("");
  const [staffRole, setStaffRole] = useState<string>("All");
  const [selectedProject, setSelectedProject] = useState<AutoCompleteUIDTO | undefined>({ label: "All", externalId: "All" });
  const [sitesFromArchivedProjects, setSitesFromArchivedProjects] = useLocalStorage<boolean>(
    STORAGE_KEYS.STORED_SHOW_ARCHIVED_PROJECTS,
    false,
  );
  const { onDataFetch, forceUpdateOn, isLoading, openCreateModal, selectedFile, siteActions } = useSiteList({
    projectExternalId: selectedProject?.externalId || "All",
    role: staffRole,
    title: search,
    isArchived: !!sitesFromArchivedProjects,
  });
  const [isOpen, setIsOpen] = useState(false);
  const { isMobileWidth } = useWindowSize();
  const { bottomSectionRef, calculateMaxHeight } = useTableHeight();

  const searchParams = new URLSearchParams(useLocation().search);
  const titleFilter = searchParams.get("title");
  const roleFilter = searchParams.get("role");
  const project = searchParams.get("project");
  const archived = searchParams.get("archived");
  const { updateUrl } = useUpdateUrl(Routes.SITE, searchParams);

  useEffect(() => {
    if (project !== null && siteActions.projects) {
      const projectUrl = siteActions.projects.find((p) => p.externalId === project);
      if (projectUrl) setSelectedProject(projectUrl);
    } else {
      searchParams.set("project", "All");
    }

    if (roleFilter !== null) {
      setStaffRole(roleFilter);
    } else {
      searchParams.set("role", "All");
    }

    if (titleFilter !== null && titleFilter !== "") {
      setSearch(titleFilter);
    }

    if (archived !== null) {
      setSitesFromArchivedProjects(archived === "true");
    } else if (sitesFromArchivedProjects !== undefined) {
      searchParams.set("archived", sitesFromArchivedProjects.toString());
    }

    updateUrl();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteActions.projects]);

  const toggleSitesFromArchivedProjects = () => {
    setSitesFromArchivedProjects((prev) => !prev);
    searchParams.set("archived", (!sitesFromArchivedProjects).toString());
    updateUrl();
  };

  const handleOnCreate = () => {};

  const renderSearchSiteTitle = (
    <TextField
      label="Site title"
      name="sitetitle"
      variant="outlined"
      value={search}
      size="small"
      onChange={(e: any) => {
        setSearch(e.target.value);
        searchParams.set("title", e.target.value);
        updateUrl();
      }}
      style={{ marginLeft: "20px", marginRight: "20px", minWidth: "400px" }}
    />
  );
  const renderFilters = () => {
    const mobileFilters = (
      <div className={classes.mobileFilters}>
        {renderSearchSiteTitle}
        <FilterDialogButton isOpen={isOpen} setIsOpen={setIsOpen} />
      </div>
    );

    const desktopFilters = (
      <div className={classes.desktopFilters}>
        {renderSearchSiteTitle}
        {renderDialogFilters}
      </div>
    );
    return isMobileWidth() ? mobileFilters : desktopFilters;
  };

  const renderDialogFilters = (
    <>
      <SelectComponent
        label={"Roles"}
        name="roles"
        value={staffRole}
        setValue={(role: string) => {
          setStaffRole(role);
          searchParams.set("role", role);
          updateUrl();
        }}
        options={Object.values(SitesRoleOptions)}
        size="small"
      />
      <Autocomplete
        disablePortal
        options={
          RoleUtils.isAdmin()
            ? [{ label: "All", externalId: "All" }, ...siteActions.projects]
            : [{ label: "All", externalId: "All" }, ...siteActions.projectsManager]
        }
        getOptionLabel={(option) => option.label}
        className="project-name-select"
        size="small"
        value={selectedProject}
        onChange={(event, newValue) => {
          if (newValue) {
            setSelectedProject(newValue);
            searchParams.set("project", newValue.externalId);
            updateUrl();
          }
        }}
        renderInput={(params) => <TextField {...params} label="Project Name" variant="outlined" />}
        disableClearable
      />

      <Box marginLeft="1em">
        <BooleanSegmented
          checked={sitesFromArchivedProjects}
          onToggle={toggleSitesFromArchivedProjects}
          offLabel="Active"
          onLabel="Archived"
          label="Status"
          ariaLabel="Archived projects"
        />
      </Box>
    </>
  );

  function getAdditionalActions(instance: Site): ActionsMenuEntryIcons[] {
    if (!RoleUtils.isAdminOrManager())
      return [
        {
          title: t("N/A"),
          action: () => {},
        },
      ];
    const options = [
      {
        title: t("View"),
        action: () => siteActions.handleOpenViewDialog(instance),
        icon: <VisibilityIcon />,
      },
      {
        title: t("Edit"),
        action: () => siteActions.handleOpenEditDialog(instance),
        icon: <EditIcon />,
      },
      {
        title: t("Delete"),
        action: () => siteActions.setSelectedSite(instance),
        icon: <DeleteOutlineIcon />,
      },
    ];

    return options;
  }

  return (
    <AppContainerFullHeight
      bottomSectionRef={bottomSectionRef}
      topSection={
        <AppToolbar title={t("Sites")} rightContent={toolbarActionsSlot}>
          {renderFilters()}
        </AppToolbar>
      }
      bottomSection={
        <Box sx={{ "&>div:first-child": { borderRadius: 8 } }}>
          <TableBasedCrud<Site>
            buttonContainer={customButtonsContainer}
            gridButtonContainer={gridButtonContainer}
            elementName={"Sites"}
            maxHeight={calculateMaxHeight(true)}
            stickyHeader
            stickyPagination
            delayFetch={search}
            rowsPerPage={ROWS_PER_PAGE}
            denseTable
            forceUpdateOn={forceUpdateOn}
            disableAutoFetch
            setIsEditModeActive={siteActions.setEditMode}
            stylesForRowIfCondition={(instance) => (instance.archived ? { backgroundColor: Token.surfaceSunken } : {})}
            renderRowIfCondition={(instance) => sitesFromArchivedProjects || !instance.archived}
            onDataFetch={onDataFetch}
            onCreate={RoleUtils.isAdminOrManager() ? () => handleOnCreate() : undefined}
            validateBeforeClosing
            addButtonBuilder={
              RoleUtils.isAdminOrManager()
                ? addButtonInToolbar(() => (
                    <Button
                      startIcon={<AddIcon />}
                      sx={{ backgroundColor: Token.actionPrimary, color: Token.textOnAction }}
                      onClick={() => {
                        siteActions.setOpenCreateModal(true);
                      }}
                    >
                      Add New Site
                    </Button>
                  ))
                : undefined
            }
            onViewMode={(instance) => siteActions.handleOpenViewDialog(instance)}
            buildAdditionalActions={(instance, refresh) => {
              return [
                {
                  title: "Edit",
                  action: () => siteActions.handleOpenEditDialog(instance),
                },
                {
                  title: t("Delete"),
                  action: () => {
                    siteActions.setSelectedSite(instance);
                  },
                },
              ];
            }}
            renderDialogContent={(register, errors, control) => <></>}
            resetValue={{ Role: null, Project: null, Title: null }}
            tableColumns={[
              {
                header: t("Image"),
                content: (instance) => (
                  <>
                    <OpsAvatarComponent
                      avatarUrl={instance.imageUrl}
                      forceGenericAvatar={true}
                      genericAvatar={<NoPhotographyIcon />}
                      sx={{ width: "2.5em", height: "2.5em" }}
                    />
                  </>
                ),
              },
              {
                header: t("Role"),
                content: (instance) => (
                  // This property should never be undefined, but if sometime this receives an undefined this conditional will prevent the view from crashing, but showing that the site has no role assigned
                  <>
                    {OpsStringUtils.capitalize(
                      instance.roleName != undefined
                        ? SitesRoleOptions[instance.roleName as keyof typeof SitesRoleOptions]
                        : "No role assigned",
                    )}
                  </>
                ),
              },
              {
                header: t("Project"),
                content: (instance) => <>{instance.projectExternalId === "All" ? "ALL" : instance.projectName}</>,
              },
              {
                header: t("Title"),
                content: (instance) => <>{instance.title!}</>,
              },
              {
                header: t("Actions"),
                content: (instance) => {
                  const actions = getAdditionalActions(instance);
                  return (
                    <>
                      {actions.map((action) => {
                        return <ActionButton onClick={action.action} tooltip={action.title} icon={action.icon} />;
                      })}
                    </>
                  );
                },
              },
            ]}
            hideActions={true} // Hide default Actions column
          />
          {/* Dialog to create sites */}
          <SiteCreateDialog
            onClose={siteActions.closeCreateDialog}
            staffRole={staffRole}
            isLoading={isLoading}
            open={openCreateModal}
            projects={siteActions.projectsAdminManager}
            handleUploadImage={siteActions.handleUploadImage}
            selectedFile={selectedFile ? selectedFile : ""}
            handleOnCreateSite={siteActions.handleOnCreateSite}
            handleImageDelete={siteActions.handleImageDelete}
          />
          {/* Dialog to view site */}
          {siteActions.viewSite && (
            <SiteViewDialog viewSite={siteActions.viewSite} onClose={() => siteActions.setViewSite(undefined)} isLoading={false} />
          )}
          {/* Dialog to edit site */}
          <SiteEditDialog
            onClose={() => siteActions.closeEditDialog()}
            handleOnUpdate={siteActions.handleOnUpdate}
            open={!!siteActions.editSite}
            isLoading={isLoading}
            projects={siteActions.projectsAdminManager}
            handleImageDelete={siteActions.handleImageDelete}
            handleUploadImage={siteActions.handleUploadImage}
            selectedFile={selectedFile}
            editSite={siteActions.editSite}
          />
          {/* Dialog to delete sites */}
          <AppDialog
            open={!!siteActions.selectedSite}
            onClose={() => siteActions.setSelectedSite(undefined)}
            title={"Delete"}
            content={
              <Typography>
                {`The next site is going to be eliminated: ${siteActions.selectedSite?.title}.
            Do you want to confirm this action?`}
              </Typography>
            }
            buttons={[
              {
                label: "Confirm delete",
                onClick: () => {
                  siteActions.handleOnDeleteSite();
                },
              },
            ]}
          />
          {/* Dialog to manage filters  */}
          <FilterDialog title={"Site"} setIsOpen={setIsOpen} isOpen={isOpen}>
            {renderDialogFilters}
          </FilterDialog>
        </Box>
      }
    />
  );
}

const useStyles = makeStyles(
  (theme) => ({
    desktopFilters: {
      ...filtersBarStyle(),
      // Scoped to the direct child (the search field) and capped: with a full-width bar an
      // uncapped 100% pushed every other filter to a second row.
      "& > label, & > div:first-child": {
        margin: "0 !important",
        minWidth: "0 !important",
        width: "100%",
        maxWidth: "320px",
      },
      "& > div ": {
        margin: "0!important",
      },
    },
    mobileFilters: {
      width: "100%",
      display: "flex",
      flexDirection: "column",
      rowGap: "10px",
      "&> div": {
        margin: "0 !important",
      },
      "&>div:first-child": {
        minWidth: "0 !important",
      },
    },
    [theme.breakpoints.down("md")]: {
      desktopFilters: {
        display: "grid",
        gridTemplateColumns: "2fr 1fr 1fr",
        gridRowGap: "10px",
        gridColumnGap: "10px",
      },
    },
  }),
  { index: 1 },
);
